<template>
	<div class="rte-stream-list">
		<LoadingPlaceholder2 v-if="!( isRegistrySynced && haveSubDocsSynced )" />
		<div v-else-if="!subDocs.length" class="empty-placeholder" >
			<SharedStreamPlaceholder
				v-if="docType === 'shared'"
				@takeNotes="$emit( `takeNotes` )"
				@scheduleMeeting="$emit( `scheduleMeeting` )"
			/>
			<EmptyPrivateStream
				v-else v-on="bubble( 'newNote' )"
			/>
		</div>
		<div v-else class="rte-stream-list__stream-container-outer">
			<RteStreamInstance
				class="rte-stream-list__instance"
				v-for="(subDoc, index) in subDocs"
				:autofocus="subDoc.autofocus"
				:can-recur="subDoc.canRecur"
				:context="context"
				:creator="subDoc.creator"
				:date="subDoc.date"
				:doc-type="docType"
				:documentName="documentName"
				:has-recurrence="subDoc.hasRecurrence"
				:is-assistant-enabled="subDoc.isAssistantEnabled"
				:is-identified="subDoc.isIdentified"
				:key="subDoc.subDocId"
				:meeting-template-id="subDoc.meetingTemplateId"
				:order="index"
				:sub-doc-id="subDoc.subDocId"
				:managed-by="subDoc.managedBy"
				:recurrence-id="subDoc.recurrenceId"
				:recurrence-entry-id="subDoc.recurrenceEntryId"
				:title="subDoc.title"
				:transcription-itemized-summary="subDoc.transcriptionItemizedSummary"
				:transcription-origin="subDoc.transcriptionOrigin"
				:transcription-rating="subDoc.transcriptionRating"
				:transcription-status-cycle="subDoc.transcriptionStatusCycle"
				:transcription-status="subDoc.transcriptionStatus"
				:transcription-sub-status="subDoc.transcriptionSubStatus"
				:transcription-summary="subDoc.transcriptionSummary"
				:virtual-meeting-url="subDoc.virtualMeetingUrl"
				v-on="bubble(
					`addAssistant`,
					`assignVirtualMeetingURL`,
					`assistMeeting`,
					`cancelJoin`,
					`changeDate`,
					`collapseDetails`,
					`copyLink`,
					`created`,
					`deleteNote`,
					`expandDetails`,
					`forceJoin`,
					`hideAssistant`,
					`joinMeeting`,
					`rateTranscription`,
					`recommendActionItem`,
					`saveAsTemplate`,
					`scheduleRecurrence`,
					`teamingExtensionChange`,
					`titleChange`,
					`transcribe`,
					`unassistMeeting`,
					`viewTranscription`,
					`checkZoomOauth`,
				)"
			>
				<template #footer="{ isEmpty }">
					<slot name="newNoteFooter" :sub-doc="subDoc" :is-first="index === 0" :is-empty="isEmpty"></slot>
				</template>
			</RteStreamInstance>
			<footer class="rte-stream-list__end-cap">
				<div class="rte-stream-list__end-cap-line rte-stream-list__end-cap-line-left"><hr></div>
				<div class="rte-stream-list__end-cap-text">fin</div>
				<div class="rte-stream-list__end-cap-line rte-stream-list__end-cap-line-right"><hr></div>
			</footer>
		</div>
	</div>
</template>

<script>
import debounce from "lodash/debounce";
import { mapGetters, mapMutations } from "vuex";

import {
	EventsMixin,
	LoadingPlaceholder2
} from "@teaming/common-ui";

import RteStreamInstance from "./RteStreamInstance";
import SharedStreamPlaceholder from "./SharedStreamPlaceholder.vue";
import EmptyPrivateStream from "./EmptyPrivateStream.vue";

export default {
	name: "RteStreamList",
	teamingPackage: "common-team",
	mixins: [
		EventsMixin,
	],
	emits: [
		"addAssistant",
		"assignVirtualMeetingURL",
		"assistMeeting",
		"cancelJoin",
		"changeDate",
		"collapseDetails",
		"copyLink",
		"created",
		"deleteNote",
		"expandDetails",
		"forceJoin",
		"joinMeeting",
		"newNote",
		"rateTranscription",
		"saveAsTemplate",
		"scheduleRecurrence",
		"selectTemplate",
		"takeNotes",
		"teamingExtensionChange",
		"titleChange",
		"transcribe",
		"unassistMeeting",
		"viewTranscription",
	],
	components: {
		LoadingPlaceholder2,
		EmptyPrivateStream,
		SharedStreamPlaceholder,
		RteStreamInstance,
	},
	props: {
		context: {
			type: Object,
			required: true,
		},
		docType: {
			type: String,
			required: true,
		},
		documentName: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			isRegistryInitialized: false,
			isRegistryConnected: false,
			isRegistrySynced: false,
			slowComputeSubDocs: null,
		};
	},
	computed: {
		...mapGetters( "TeamlikeStream", [
			"subDocs",
			"haveSubDocsSynced",
		] ),
	},
	methods: {
		...mapMutations( "TeamlikeStream", [
			"setSubDocs",
		] ),
		// computeSubDocs gets registered on the EventEmitter `on( `${ documentName }.subDocs`, this.computeSubDocs )`
		// this event is emitted when the hocuspocus provider is synced with the ydoc
		async computeSubDocs() {
			const { documentName, } = this;
			const registry = this.$documentRegistry();
			if ( !registry.isConnected( this.documentName ) ) {
				return;
			}
			const manager = registry.manage( documentName );
			const subDocsInYdoc = manager.getSubDocs();
			this.setSubDocs( { subDocs: subDocsInYdoc, } );
			await this.$nextTick();
			this.isRegistrySynced = true;
		},
		async tryDisposeDocumentRegistry() {
			const registry = this.$documentRegistry();
			if ( !registry.isConnected( this.documentName ) ) {
				return;
			}
			registry.disconnect( this.documentName );
			await this.$nextTick();
			this.slowComputeSubDocs = null;
			this.isRegistryConnected = false;
			this.isRegistryInitialized = false;
			this.isRegistrySynced = false;
		},
		async tryInitializeDocumentRegistry() {
			const { documentName, isRegistryInitialized, } = this;
			if ( isRegistryInitialized || !documentName ) {
				return;
			}
			this.slowComputeSubDocs = debounce( () => {
				this.computeSubDocs();
				// eslint-disable-next-line no-magic-numbers
			}, 150 );
			const registry = this.$documentRegistry();
			registry.on( `${ documentName }.connected`, this.onDocumentManagerConnected );
			registry.on( `${ documentName }.subDocs`, this.slowComputeSubDocs );
			registry.connect( documentName );
			await this.$nextTick();
			this.isRegistryInitialized = true;
		},
		async onDocumentManagerConnected() {
			await this.$nextTick();
			this.isRegistryConnected = true;
		},
	},
	mounted() {
		this.tryInitializeDocumentRegistry();
	},
	updated() {
		this.tryInitializeDocumentRegistry();
	},
	beforeUnmount() {
		this.tryDisposeDocumentRegistry();
	},
};
</script>

<style lang="scss">
$rte-stream-list-collab-cursor-border-color: $color1-900;
$rte-stream-list-collab-cursor-font-color: $white;

.collaboration-cursor__caret {
	position: relative;
	margin-left: -0.0625rem;
	margin-right: -0.0625rem;
	border-left: 0.0625rem solid $rte-stream-list-collab-cursor-border-color;
	border-right: 0.0625rem solid $rte-stream-list-collab-cursor-border-color;
	word-break: normal;
	pointer-events: none;
}
/* Render the username above the caret */
.collaboration-cursor__label {
	position: absolute;
	top: -1.4em;
	left: -0.0625rem;
	font-size: .75rem;
	font-style: normal;
	font-weight: 600;
	line-height: normal;
	user-select: none;
	color: $rte-stream-list-collab-cursor-font-color;
	padding: 0.1rem 0.3rem;
	border-radius: .1875rem .1875rem .1875rem 0;
	white-space: nowrap;
}

// this class is applied to image nodes
.teaming-text-editor__image {
	// keeps the whole image visible at lower breakpoints
	max-width: 100%;
}
</style>

<style lang="scss" scoped>
$rte-stream-list-end-cap-line-color: $color1-200;
$rte-stream-list-end-cap-text-color: $color1-700;

.rte-stream-list {
	text-align: left;
	display: flex;
	flex-direction: column;
	height: 100%;
}

.rte-stream-list__stream-container-outer {
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1.5rem;
}

.rte-stream-list__end-cap {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 8rem;
}

.rte-stream-list__end-cap-text {
	@include font-and-line-for(-1);
	font-weight: 600;
	color: $rte-stream-list-end-cap-text-color;
	padding-left: 0.5rem;
	padding-right: 0.5rem;
}

.rte-stream-list__end-cap-line {
	hr {
		width: 8rem;
		height: 0.0625rem;
		background-color: $rte-stream-list-end-cap-line-color;
		border: none;
	}
}

.rte-stream-list__end-cap-line-left {
	text-align: left;
}

.rte-stream-list__end-cap-line-right {
	text-align: right;
}

.rte-stream-list .loading-placeholder {
	display: flex;
	align-content: center;
	justify-content: center;
	height: 100%;
	align-items: center;
}

.rte-stream-list .empty-placeholder {
	padding: 1rem;
}

</style>
